// ============================================================================
// FORMATO REGIONAL (LOCALE) DE DATOS DE PROYECTO
// ============================================================================
// Punto ÚNICO de formateo numérico de la aplicación. Todo lo que se muestre en
// pantalla, se exporte a PDF o se rotule en Excel debe pasar por acá.
//
// Problema que resuelve
// --------------------
// Antes convivían dos formateos incompatibles en la misma tabla:
//   `Number(v).toFixed(1)`            → "189.7"   (punto = decimal)
//   `Math.round(v).toLocaleString()`  → "2.737"   (punto = miles)
// El mismo carácter significaba dos cosas distintas en columnas vecinas, así
// que "2.737" era ambiguo (¿2737 o 2,737?). Con `Intl.NumberFormat` y un
// locale explícito el separador decimal y el de miles son siempre distintos:
//   es-CL → 189,7 · 0,97 · 2.737 · 20.052
//
// Reglas de uso (OBLIGATORIAS para PDF, Excel y nuevos reportes)
// --------------------------------------------------------------
//   1. Los valores internos SIEMPRE se mantienen como `Number`. El locale se
//      aplica sólo en la capa de presentación/exportación, nunca en el cálculo
//      ni en el transporte (JSON de la API).
//   2. Nada de `toFixed()` ni `toLocaleString()` sueltos en componentes:
//      se usa `formatNumber()` (o los alias `fmt0/fmt1/fmt2`, `formatInteger`).
//   3. En Excel NO se escriben strings ya formateados: se escribe el `Number`
//      crudo y se le adosa un `numFmt` (ver `excelNumberFormat`), para que las
//      fórmulas y los gráficos de la planilla sigan funcionando y para que
//      Excel muestre los separadores según la configuración regional del que
//      abre el archivo.
//   4. El locale es un parámetro, no una constante incrustada: la firma
//      `(value, locale)` permite forzar un locale puntual, pero el default de
//      todas las funciones es el locale del PROYECTO ACTIVO.
//
// Locale por proyecto (CONECTADO)
// -------------------------------
// `activeLocale` ya NO es una constante: lo setea `applyProjectLocale()` de
// utils/localeBootstrap.js a partir de `proyectos[].locale` del token, en el
// arranque de la app, en el login y en cada cambio de proyecto.
//
// Es un `ref` de Vue A PROPÓSITO: siendo una variable plana de módulo, cambiar
// de proyecto actualizaba el valor pero NO re-renderizaba ningún `computed`
// que dependiera de él (Vue no puede rastrear una variable de módulo). Al ser
// un ref, cualquier `computed` que llame a estas funciones se invalida solo.

import { ref } from 'vue';
import { DEFAULT_LOCALE, resolveLocale } from './localeConfig';

// Locale por defecto de todo el sistema. Se conserva el nombre GLOBAL_LOCALE
// por compatibilidad con los imports existentes, pero el valor ahora viene de
// la fuente única (utils/localeConfig.js) en vez de una constante duplicada
// que contradecía a la de utils/auth.js.
export const GLOBAL_LOCALE = DEFAULT_LOCALE;

// Locale efectivo (reactivo). Arranca en el default y lo reemplaza
// applyProjectLocale() con el Formato Regional del proyecto activo.
const activeLocale = ref(GLOBAL_LOCALE);

/**
 * `ref` del locale vigente, por si un componente necesita declarar la
 * dependencia reactiva de forma explícita o watchear el cambio.
 * No hace falta para el caso normal: llamar a `formatNumber`/`fmt*` dentro de
 * un `computed` ya lee el ref (vía getActiveLocale) y registra la dependencia.
 */
export const localeRef = activeLocale;

// Locale vigente: es el default real de todas las funciones de este módulo.
// Leer `.value` acá es lo que hace que los computed se re-evalúen al cambiarlo.
export function getActiveLocale() {
  return activeLocale.value;
}

// Cambia el locale vigente. Se valida contra Intl para no dejar la app con un
// locale inválido (un BCP-47 malformado hace throw en Intl.NumberFormat).
export function setGlobalLocale(locale) {
  if (!locale) { activeLocale.value = GLOBAL_LOCALE; return activeLocale.value; }
  try {
    new Intl.NumberFormat(locale);
    activeLocale.value = resolveLocale(locale);
  } catch (_) {
    activeLocale.value = GLOBAL_LOCALE;
  }
  return activeLocale.value;
}

// Texto que se muestra cuando el valor no es un número representable
// (null, undefined, '', NaN, Infinity).
export const EMPTY_VALUE = '—';

// Opciones por defecto: sin relleno de decimales y hasta 2 decimales, con
// separador de miles. Da exactamente los casos de referencia de es-CL:
//   189.7 → "189,7"   0.97 → "0,97"   2737 → "2.737"   20052 → "20.052"
const DEFAULT_OPTIONS = { minimumFractionDigits: 0, maximumFractionDigits: 2, useGrouping: true };

// Instanciar Intl.NumberFormat es caro y estas funciones se llaman por celda
// de tabla, así que se memoiza por (locale + opciones).
const formatterCache = new Map();

function getFormatter(locale, options) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.NumberFormat(locale, options);
    } catch (_) {
      formatter = new Intl.NumberFormat(GLOBAL_LOCALE, options);
    }
    formatterCache.set(key, formatter);
  }
  return formatter;
}

// Traduce las opciones "de negocio" (decimals, grouping) a opciones de Intl.
function resolveIntlOptions(options = {}) {
  const { decimals, minDecimals, maxDecimals, grouping, ...rest } = options;
  const intl = { ...DEFAULT_OPTIONS, ...rest };

  // `decimals` fija la cantidad exacta (equivalente a toFixed(n) pero con
  // separadores correctos); minDecimals/maxDecimals permiten un rango.
  if (Number.isFinite(decimals)) {
    intl.minimumFractionDigits = decimals;
    intl.maximumFractionDigits = decimals;
  }
  if (Number.isFinite(minDecimals)) intl.minimumFractionDigits = minDecimals;
  if (Number.isFinite(maxDecimals)) intl.maximumFractionDigits = maxDecimals;
  if (grouping !== undefined) intl.useGrouping = !!grouping;

  // Intl exige min <= max; si el llamador los cruza, manda el máximo.
  if (intl.minimumFractionDigits > intl.maximumFractionDigits) {
    intl.maximumFractionDigits = intl.minimumFractionDigits;
  }
  return intl;
}

/**
 * Formatea un número según el locale indicado.
 *
 * @param {number|string|null} value  valor a formatear (se castea con Number)
 * @param {string} [locale]           BCP-47; por defecto el locale vigente
 * @param {object} [options]          { decimals, minDecimals, maxDecimals,
 *                                      grouping, fallback } + opciones de Intl
 * @returns {string} valor formateado, o `fallback` si no es un número finito
 */
export function formatNumber(value, locale = getActiveLocale(), options = {}) {
  const num = typeof value === 'number' ? value : Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(num)) {
    return options.fallback !== undefined ? options.fallback : EMPTY_VALUE;
  }
  return getFormatter(locale, resolveIntlOptions(options)).format(num);
}

// Entero con separador de miles: 20052 → "20.052". Redondea (no trunca), igual
// que el `Math.round(...).toLocaleString(...)` que se usaba antes.
export function formatInteger(value, locale = getActiveLocale(), options = {}) {
  return formatNumber(value, locale, { ...options, decimals: 0 });
}

// Alias de decimales fijos, para reemplazar 1:1 los viejos toFixed(0|1|2).
export const fmt0 = (value, locale = getActiveLocale()) => formatNumber(value, locale, { decimals: 0 });
export const fmt1 = (value, locale = getActiveLocale()) => formatNumber(value, locale, { decimals: 1 });
export const fmt2 = (value, locale = getActiveLocale()) => formatNumber(value, locale, { decimals: 2 });

// Porcentaje: 0.9 → "90%" (fraction) o 90 → "90%" (ya en escala 0-100).
export function formatPercent(value, locale = getActiveLocale(), options = {}) {
  const { fraction = false, decimals = 0, ...rest } = options;
  const num = Number(value);
  if (!Number.isFinite(num)) return rest.fallback !== undefined ? rest.fallback : EMPTY_VALUE;
  return `${formatNumber(fraction ? num * 100 : num, locale, { ...rest, decimals })}%`;
}

// ----------------------------------------------------------------------------
// Formateo recursivo de estructuras
// ----------------------------------------------------------------------------
// Útil para armar payloads de reporte/exportación de una sola pasada. OJO: el
// resultado tiene strings donde había números, así que se usa al final de la
// cadena (justo antes de renderizar/escribir), nunca sobre datos que después
// vayan a entrar a un cálculo.
//
// `options.skip` lista claves que NO deben formatearse (ids, años, códigos:
// un id 20052 no debe salir como "20.052"). Se comparan por nombre de clave a
// cualquier profundidad.
const DEFAULT_SKIP_KEYS = ['id', 'proyecto_id', 'proyectoId', 'cuartel_id', 'cuartelId', 'sensor_id', 'sensorId', 'station_id', 'stationId', 'anio', 'year'];

function shouldSkip(key, skip) {
  return key !== undefined && skip.includes(key);
}

function formatDeep(data, locale, options, key) {
  const { skip = DEFAULT_SKIP_KEYS } = options;

  if (typeof data === 'number') {
    if (shouldSkip(key, skip)) return data;
    return formatNumber(data, locale, options);
  }
  if (data === null || data === undefined) return data;
  if (data instanceof Date) return data;
  if (Array.isArray(data)) return data.map((item) => formatDeep(item, locale, options, key));
  if (typeof data === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(data)) out[k] = formatDeep(v, locale, options, k);
    return out;
  }
  return data;
}

/**
 * Formatea recursivamente todos los números de un objeto. No muta la entrada.
 * @param {object} data
 * @param {string} [locale]
 * @param {object} [options] mismas opciones que formatNumber + { skip: [] }
 */
export function formatObject(data, locale = getActiveLocale(), options = {}) {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) return data;
  return formatDeep(data, locale, options);
}

/**
 * Formatea recursivamente todos los números de un arreglo (de escalares, de
 * objetos o mixto). No muta la entrada.
 */
export function formatArray(data, locale = getActiveLocale(), options = {}) {
  if (!Array.isArray(data)) return data;
  return formatDeep(data, locale, options);
}

// ----------------------------------------------------------------------------
// Exportación a Excel
// ----------------------------------------------------------------------------
// Los patrones de `numFmt` de OOXML son locale-independientes: se escriben
// SIEMPRE con "," de miles y "." decimal, y Excel los renderiza con los
// separadores de la configuración regional de quien abre el archivo. Por eso
// acá no se traduce nada — la única regla es escribir el Number crudo, no el
// string ya formateado, para no romper fórmulas ni gráficos.
export function excelNumberFormat(decimals = 2, { grouping = true } = {}) {
  const dec = Math.max(0, Math.min(20, Number(decimals) || 0));
  const intPart = grouping ? '#,##0' : '0';
  return dec > 0 ? `${intPart}.${'0'.repeat(dec)}` : intPart;
}

/**
 * Celda de Excel para SheetJS: Number crudo + numFmt, o texto si el valor no
 * es numérico (así una celda "—" no se convierte en 0).
 * @param {number|string|null} value
 * @param {number} decimals cantidad de decimales a MOSTRAR (el valor no se altera)
 */
export function excelNumericCell(value, decimals = 2, { grouping = true } = {}) {
  const num = typeof value === 'number' ? value : Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(num)) {
    return { t: 's', v: typeof value === 'string' && value ? value : EMPTY_VALUE };
  }
  return { t: 'n', v: num, z: excelNumberFormat(decimals, { grouping }) };
}

// ----------------------------------------------------------------------------
// Inverso: leer un número ya formateado
// ----------------------------------------------------------------------------
// Sólo para interoperar con datos legados que llegan como string localizado
// (por ejemplo campos que la API todavía formatea antes de responder). Código
// nuevo NO debería necesitarla: los números deben viajar como Number.
export function parseFormattedNumber(text, locale = getActiveLocale()) {
  if (typeof text === 'number') return Number.isFinite(text) ? text : null;
  if (text === null || text === undefined || text === '') return null;

  const parts = getFormatter(locale, DEFAULT_OPTIONS).formatToParts(12345.6);
  const group = parts.find((p) => p.type === 'group')?.value ?? '.';
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? ',';

  const normalized = String(text)
    .trim()
    .split(group).join('')
    .replace(decimal, '.')
    .replace(/[^0-9.eE+-]/g, '');

  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

export default {
  GLOBAL_LOCALE,
  EMPTY_VALUE,
  localeRef,
  getActiveLocale,
  setGlobalLocale,
  formatNumber,
  formatInteger,
  formatPercent,
  formatObject,
  formatArray,
  fmt0,
  fmt1,
  fmt2,
  excelNumberFormat,
  excelNumericCell,
  parseFormattedNumber
};
